import classNames from "classnames";
import React from "react";
import { connect } from "react-redux";
import { t } from "ttag";

import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { IBasket } from "../../../models/catalogue.interfaces";
import { IBasketLineAPIURL } from "../../../models/nominals";
import { TStateMapper } from "../../reducers.interfaces";
import { BasketLineMinimal } from "../BasketLine";
import { ExpeditedShippingDisclosure } from "../components/ExpeditedShippingDisclosure";
import { SHIPPING_METHOD_NAMES, Step } from "../constants";
import { IReduxFormState } from "../reducers.interfaces";
import {
    ILineOptionGroupingData,
    getLineOptionGroupingData,
} from "../selectors";

interface IOwnProps {
    onEdit?: (event?: React.MouseEvent<HTMLElement>) => void;
}

interface IReduxProps {
    form: IReduxFormState;
    basket: IBasket | null;
    groups: ILineOptionGroupingData[];
    showSummary: boolean;
}

interface IProps extends IOwnProps, IReduxProps {}

interface IState {}

// TODO(ssr, LEVEL 4 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 4/10 due to Redux connect, conditional rendering logic, and summary generation.
class ShippingMethodSummaryComponent extends React.Component<IProps, IState> {
    private buildLine(lineURL: IBasketLineAPIURL) {
        if (!this.props.basket) {
            return null;
        }
        const line = this.props.basket.lines.find((l) => {
            return l.url === lineURL;
        });
        if (!line || !line.quantity) {
            return null;
        }
        // Don't show bundle lines (any line which has children)
        const children = this.props.basket.lines.filter(
            (l) => l.parent === line.id,
        );
        if (children.length) {
            return;
        }
        const isBundled =
            this.props.basket.lines.filter((l) => l.id === line.parent).length >
            0;

        return (
            <BasketLineMinimal
                key={`${lineURL}`}
                line={line}
                isBundled={isBundled}
            />
        );
    }

    private buildSummary() {
        if (!this.props.showSummary) {
            return null;
        }
        return this.props.groups.map((group, i) => {
            const method =
                group.methods.filter((m) => {
                    return this.props.form.shipping_method === m.code;
                })[0] || group.methods[0];

            const lines = group.lines.map(this.buildLine.bind(this));
            return (
                <div key={i} className="checkout-step__shipping-group-summary">
                    {/* TODO(i18n): SHIPPING_METHOD_NAMES returns hardcoded strings - convert to function using t tagged template. Also wrap fallback method.name in t template: {t`${method.name}`} */}
                    <h3 className="checkout-step__label">
                        {SHIPPING_METHOD_NAMES[method.name] || method.name}
                    </h3>
                    {/* TODO(i18n): method.description comes from API - ensure backend wraps shipping method descriptions in gettext */}
                    <p>{method.description}</p>
                    <ul>{lines}</ul>
                </div>
            );
        });
    }

    render() {
        const summaryClasses = classNames({
            "checkout-step__summary": true,
            "checkout-step__summary--collapsed": !this.props.showSummary,
        });
        if (this.props.groups.length <= 0) {
            return (
                <div className={summaryClasses}>
                    <LoadingSpinner />
                </div>
            );
        }
        return (
            <div className={summaryClasses}>
                {this.buildSummary()}
                <ExpeditedShippingDisclosure groups={this.props.groups} />
                {this.props.onEdit && (
                    <p>
                        <a
                            className="checkout-step__back"
                            href="#"
                            onClick={this.props.onEdit}
                        >
                            {t`Edit`}
                        </a>
                    </p>
                )}
            </div>
        );
    }
}

const mapStateToProps: TStateMapper<"checkout", IReduxProps, IOwnProps> = (
    rootState,
    ownProps,
) => {
    const state = rootState.checkout;
    return {
        form: state.form,
        basket: state.data.basket,
        groups: getLineOptionGroupingData(state),
        showSummary: state.form.current_step > Step.SHIPPING_METHOD,
        ...ownProps,
    };
};

export const ShippingMethodSummary = connect(mapStateToProps)(
    ShippingMethodSummaryComponent,
);
